import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Lock, ArrowRight, Megaphone, Star, PackageCheck, Scale, ShieldCheck,
    Store, Gavel, Wallet, Sparkles, TrendingUp,
} from "lucide-react";

const BENEFITS = [
    {
        icon: Megaphone,
        title: "Exposure",
        body: "Put your inventory in front of motivated, verified buyers actively looking to win. Every listing gets a fair, equal shot in front of the whole marketplace — no pay-to-play feed games.",
    },
    {
        icon: Star,
        title: "Reviews & reputation",
        body: "Build a public track record with every successful sale. Verified seller status and genuine buyer reviews compound into trust that wins you more bids over time.",
    },
    {
        icon: PackageCheck,
        title: "Inventory movement",
        body: "Move overstock, seasonal, and end-of-line stock quickly. A built-in timer and max-bidder cap create real urgency, so items sell on a predictable schedule instead of sitting.",
    },
    {
        icon: Scale,
        title: "Fair pricing",
        body: "Sealed, one-shot bids surface what buyers are genuinely willing to pay — true market value. No artificial suppression from visible-bid anchoring, sniping, or bot interference.",
    },
    {
        icon: ShieldCheck,
        title: "No race-to-the-bottom discounts",
        body: "Stop training customers to wait for the next coupon. Blind bidding protects your margins and brand by competing on value, not on who can slash prices the deepest.",
    },
];

const STEPS = [
    { icon: Store, title: "List your item", body: "Create a sealed-bid auction in minutes with AI-assisted descriptions. Set your starting price, timer, and bidder cap." },
    { icon: Gavel, title: "Receive sealed bids", body: "Buyers each submit one honest, hidden bid. The auction closes on your timer — or the moment the bidder cap is reached." },
    { icon: Wallet, title: "Get paid", body: "The highest bid wins, the buyer checks out securely, and your net payout lands after our transparent marketplace fee." },
];

export default function Business() {
    return (
        <div>
            {/* Hero */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_15%_0%,rgba(46,139,255,0.22)_0%,rgba(46,139,255,0)_60%),radial-gradient(50%_50%_at_90%_10%,rgba(240,180,41,0.10)_0%,rgba(240,180,41,0)_55%)]" />
                <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:py-20">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                        <Lock className="h-3.5 w-3.5 text-electric" /> For Businesses &amp; Sellers
                    </span>
                    <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
                        Why Businesses Join <span className="text-brass italic">OmniAuc</span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground">
                        Sell to motivated buyers, move inventory on your schedule, and discover true market value &mdash; all without the discount death spiral. Blind bidding works for your margins, not against them.
                    </p>
                    <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                        <Button asChild className="rounded-[10px]" data-testid="business-cta-start">
                            <Link to="/signup">Start selling<ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                        <Button asChild variant="secondary" className="rounded-[10px]" data-testid="business-cta-compare">
                            <Link to="/compare">See how we compare</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Five reasons sellers choose us</h2>
                    <p className="mt-3 text-sm text-muted-foreground">A fairer mechanism that&apos;s better for buyers and better for your business.</p>
                </div>

                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {BENEFITS.map((b) => {
                        const Icon = b.icon;
                        return (
                            <div
                                key={b.title}
                                data-testid="business-benefit"
                                className="group rounded-2xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-brass/30 bg-brass/10 text-brass">
                                    <Icon className="h-5 w-5" aria-hidden />
                                </span>
                                <h3 className="mt-4 font-display text-lg font-semibold">{b.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
                            </div>
                        );
                    })}

                    {/* Inline CTA card to fill the 6th grid cell on large screens */}
                    <div className="flex flex-col justify-center rounded-2xl border border-electric/30 bg-electric/[0.07] p-6" data-testid="business-benefit-cta">
                        <Sparkles className="h-5 w-5 text-electric" aria-hidden />
                        <h3 className="mt-4 font-display text-lg font-semibold">Ready to list?</h3>
                        <p className="mt-2 text-sm text-muted-foreground">Get verified and publish your first sealed-bid auction today.</p>
                        <Button asChild className="mt-4 w-full rounded-[10px]" data-testid="business-benefit-cta-btn">
                            <Link to="/signup">Become a seller<ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* How selling works */}
            <section className="border-y border-border bg-secondary/20">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">How selling works</h2>
                        <p className="mt-3 text-sm text-muted-foreground">From listing to payout in three simple steps.</p>
                    </div>
                    <div className="mt-10 grid gap-5 md:grid-cols-3">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.title} data-testid="business-step" className="relative rounded-2xl border border-border bg-card p-6">
                                    <span className="absolute right-5 top-5 font-display text-3xl font-bold text-border">{i + 1}</span>
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary/50 text-electric">
                                        <Icon className="h-5 w-5" aria-hidden />
                                    </span>
                                    <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Closing CTA */}
            <section className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
                <div className="rounded-2xl border border-border bg-card p-10 shadow-[0_12px_30px_rgba(0,0,0,0.25)]">
                    <TrendingUp className="mx-auto h-7 w-7 text-brass" aria-hidden />
                    <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">Grow your business the fair way</h2>
                    <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground">Join verified sellers using sealed-bid auctions to move inventory and protect their margins.</p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                        <Button asChild className="rounded-[10px]" data-testid="business-cta-bottom">
                            <Link to="/signup">Create your seller account<ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                        <Button asChild variant="secondary" className="rounded-[10px]">
                            <Link to="/auctions">Browse live auctions</Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
